import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { PortfolioCentralRateGroupOverview } from '@mews-commander/data-access';
import { lt } from '@mews/framework';
import { TabbedContent } from '@mews-commander/ui';
import { Title } from '@optimus-web/core';
import { DeleteCentralRateGroupContainer } from '../../sections/components/rateGroup/DeleteCentralRateGroupContainer';
import { SelectedRateGroupContextProvider } from '../../../contexts/CurrentRateGroupContext';
import { EditCentralRateGroupContainer } from '../../sections/components/rateGroup/EditCentralRateGroupContainer';
import { ModalSidePanel } from '../../../../../components';
import { useCentralRateGroupWithProfilesGet } from './sidePanelTabs/queries';
import { PropertiesTabContent } from './sidePanelTabs/PropertiesTabContent';
import { DetailsTabContent, PoliciesTabContent } from './sidePanelTabs';
import { RateGroupTabValues } from './types';

const RATE_GROUP_TABS: NonEmptyArray<string> = ['details', 'policies', 'properties'];
interface Props {
    closeSidePanel: () => void;
    initialTab?: RateGroupTabValues;
    selectedRateGroup?: PortfolioCentralRateGroupOverview;
}
export const CentralRateGroupSidePanel = ({ closeSidePanel, initialTab, selectedRateGroup }: Props) => {
    const { data, isFetching } = useCentralRateGroupWithProfilesGet(
        { rateGroupId: selectedRateGroup?.id },
        { enabled: !!selectedRateGroup }
    );

    return (
        <ModalSidePanel
            headerActions={
                data && selectedRateGroup ? (
                    <DeleteCentralRateGroupContainer rateGroupId={selectedRateGroup.id} onSuccess={closeSidePanel} />
                ) : null
            }
            content={
                <TabbedContent
                    tabs={RATE_GROUP_TABS}
                    tabLabels={{
                        details: lt('DetailPlural'),
                        policies: lt('GeneralPolicyPlural'),
                        properties: lt('Properties'),
                    }}
                    initialTab={initialTab}
                    inline
                    showDivider
                >
                    {({ matchTabs }) => {
                        if (!(data && selectedRateGroup)) {
                            return null;
                        }

                        const selectedRateGroupContextValue = {
                            ...selectedRateGroup,
                            ...data,
                            isLocal: false,
                            defaultPolicy: {
                                settlementRules: data.centralSettlementRules,
                                cancellationPolicies:
                                    data.centralCancellationPolicies?.map(({ applicability, ...other }) => ({
                                        ...other,
                                        applicabilityType: applicability,
                                    })) ?? [],
                            },
                        };

                        return (
                            <SelectedRateGroupContextProvider value={selectedRateGroupContextValue}>
                                {matchTabs({
                                    details: () => (
                                        <DetailsTabContent
                                            editRateGroupButton={
                                                <EditCentralRateGroupContainer
                                                    rateGroup={data}
                                                    linkedRateGroups={selectedRateGroup.properties}
                                                />
                                            }
                                            rateGroup={selectedRateGroupContextValue}
                                            rates={selectedRateGroup.rates}
                                            serviceKey={selectedRateGroup.serviceKey}
                                        />
                                    ),
                                    policies: () => <PoliciesTabContent />,
                                    properties: () => (
                                        <PropertiesTabContent
                                            rateGroup={data}
                                            properties={data?.propertiesWithRateCounts}
                                            propertyGroups={data?.propertyGroups}
                                        />
                                    ),
                                })}
                            </SelectedRateGroupContextProvider>
                        );
                    }}
                </TabbedContent>
            }
            loading={isFetching}
            onClose={closeSidePanel}
            opened={!!selectedRateGroup}
            title={
                <Title level={2} wordBreak="break-all" ellipsis maxLines={2}>
                    {data?.name}
                </Title>
            }
            width="medium"
        />
    );
};
